import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  ExternalLink,
  Info,
  ListTree,
  GripVertical,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { useCatalogue } from "@/lib/useCatalogue";
import { IconPicker } from "@/components/admin/IconPicker";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { data, loading, reload } = useCatalogue();
  const [tab, setTab] = useState("menu"); // 'menu' | 'info'

  if (loading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <p className="font-display italic">Φόρτωση…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold">
              Ταβέρνα Βασίλης
            </p>
            <h1 className="font-display text-xl italic text-foreground sm:text-2xl">
              Διαχείριση
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="view-public"
              onClick={() => nav("/")}
              className="hidden items-center gap-1.5 rounded-full border border-border/80 bg-card/50 px-3 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold sm:flex"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Κατάλογος
            </button>
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {user?.email}
            </span>
            <button
              data-testid="logout"
              onClick={() => {
                logout();
                nav("/admin/login");
              }}
              className="flex items-center gap-1.5 rounded-full border border-border/80 bg-card/50 px-3 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
            >
              <LogOut className="h-3.5 w-3.5" />
              Έξοδος
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex gap-1 border-t border-border/40 pt-2">
            {[
              { id: "menu", label: "Κατάλογος", icon: ListTree },
              { id: "info", label: "Στοιχεία Ταβέρνας", icon: Info },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`tab-${id}`}
                onClick={() => setTab(id)}
                className={
                  "relative flex items-center gap-2 px-3 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] transition-colors sm:text-xs " +
                  (tab === id
                    ? "text-gold"
                    : "text-muted-foreground hover:text-foreground")
                }
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                {label}
                {tab === id && (
                  <motion.span
                    layoutId="tab-underline"
                    className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-gold"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        {tab === "menu" && <MenuManager data={data} reload={reload} />}
        {tab === "info" && <InfoManager info={data.info} reload={reload} />}
      </main>
    </div>
  );
}

/* ------------------------ Menu Manager ------------------------ */
function MenuManager({ data, reload }) {
  const { groups, categories } = data;
  const [activeCatId, setActiveCatId] = useState(categories[0]?.id ?? null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null); // category object

  const activeCat = categories.find((c) => c.id === activeCatId);
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
      {/* Categories sidebar */}
      <aside className="rounded-2xl border border-border/60 bg-card/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[0.65rem] uppercase tracking-[0.25em] text-gold-muted">
            Κατηγορίες
          </p>
          <button
            data-testid="add-category-btn"
            onClick={() => setAddingCategory(true)}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold transition-colors hover:bg-gold/20"
            aria-label="Νέα κατηγορία"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mb-3 flex items-center gap-1.5 px-1 text-[0.55rem] uppercase tracking-[0.22em] text-gold-muted/70">
          <GripVertical className="h-3 w-3" />
          Σύρε για αναδιάταξη
        </p>
        <div className="space-y-4">
          {groups.map((g) => {
            const groupCats = categories
              .filter((c) => c.group_id === g.id)
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            return (
              <SortableCategoryGroup
                key={g.id}
                group={g}
                categories={groupCats}
                activeCatId={activeCatId}
                onSelect={setActiveCatId}
                onReorder={async (newIds) => {
                  try {
                    await api.put(`/admin/categories/reorder`, {
                      group_id: g.id,
                      order: newIds,
                    });
                    await reload();
                  } catch (e) {
                    alert(formatApiError(e.response?.data?.detail) || e.message);
                  }
                }}
              />
            );
          })}
        </div>
      </aside>

      {/* Items panel */}
      <section className="rounded-2xl border border-border/60 bg-card/40 p-5 sm:p-6">
        {activeCat ? (
          <CategoryEditor
            category={activeCat}
            groups={groups}
            onEditMeta={() => setEditingCategory(activeCat)}
            reload={reload}
          />
        ) : (
          <p className="text-center text-muted-foreground">Επίλεξε κατηγορία</p>
        )}
      </section>

      <AnimatePresence>
        {addingCategory && (
          <CategoryFormModal
            groups={groups}
            categories={categories}
            onClose={() => setAddingCategory(false)}
            onSaved={async () => {
              await reload();
              setAddingCategory(false);
            }}
          />
        )}
        {editingCategory && (
          <CategoryFormModal
            initial={editingCategory}
            groups={groups}
            categories={categories}
            onClose={() => setEditingCategory(null)}
            onSaved={async () => {
              await reload();
              setEditingCategory(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------ Category Editor ------------------------ */
function CategoryEditor({ category, groups, onEditMeta, reload }) {
  const [editing, setEditing] = useState(null); // item being edited, or 'new'
  const [deletingCat, setDeletingCat] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  const deleteCategory = async () => {
    try {
      await api.delete(`/admin/categories/${category.id}`);
      await reload();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteItem = async (item) => {
    try {
      await api.delete(`/admin/categories/${category.id}/items/${item.id}`);
      await reload();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-border/40 pb-5">
        <div className="min-w-0">
          <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted">
            {groups.find((g) => g.id === category.group_id)?.el}
          </p>
          <h2
            data-testid="active-category-title"
            className="mt-1 font-display text-3xl italic text-foreground sm:text-4xl"
          >
            {category.el}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {category.en} · {category.items.length} προϊόντα · id:{" "}
            <code className="text-gold-muted">{category.id}</code>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="edit-category"
            onClick={onEditMeta}
            className="flex items-center gap-1.5 rounded-full border border-border/80 bg-background/40 px-3 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold"
          >
            <Pencil className="h-3.5 w-3.5" />
            Επεξεργασία
          </button>
          <button
            data-testid="delete-category"
            onClick={() => setDeletingCat(true)}
            className="flex items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-3 py-2 text-[0.65rem] uppercase tracking-widest text-destructive transition-colors hover:bg-destructive/20"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Διαγραφή
          </button>
          <button
            data-testid="add-item-btn"
            onClick={() => setEditing("new")}
            className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-gold-muted px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-widest text-background shadow-[0_4px_14px_-4px_rgba(212,170,90,0.5)] transition-transform hover:scale-[1.02]"
          >
            <Plus className="h-3.5 w-3.5" />
            Νέο προϊόν
          </button>
        </div>
      </div>

      <SortableItemList
        category={category}
        onEdit={(item) => setEditing(item)}
        onDelete={(item) => setDeletingItem(item)}
        onReorder={async (newIds) => {
          try {
            await api.put(
              `/admin/categories/${category.id}/items/reorder`,
              newIds,
            );
            await reload();
          } catch (e) {
            alert(formatApiError(e.response?.data?.detail) || e.message);
          }
        }}
      />

      <AnimatePresence>
        {editing && (
          <ItemFormModal
            categoryId={category.id}
            initial={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              await reload();
              setEditing(null);
            }}
          />
        )}
        {deletingCat && (
          <ConfirmModal
            title="Διαγραφή κατηγορίας;"
            message={`Θα διαγραφεί η κατηγορία «${category.el}» με όλα τα ${category.items.length} προϊόντα της. Δεν αναιρείται.`}
            onCancel={() => setDeletingCat(false)}
            onConfirm={async () => {
              setDeletingCat(false);
              await deleteCategory();
            }}
          />
        )}
        {deletingItem && (
          <ConfirmModal
            title="Διαγραφή προϊόντος;"
            message={`Θα διαγραφεί οριστικά το «${deletingItem.el}».`}
            onCancel={() => setDeletingItem(null)}
            onConfirm={async () => {
              const it = deletingItem;
              setDeletingItem(null);
              await deleteItem(it);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------ Item Form Modal ------------------------ */
function ItemFormModal({ categoryId, initial, onClose, onSaved }) {
  const [el, setEl] = useState(initial?.el ?? "");
  const [en, setEn] = useState(initial?.en ?? "");
  const [descEl, setDescEl] = useState(initial?.descEl ?? "");
  const [descEn, setDescEn] = useState(initial?.descEn ?? "");
  const [price, setPrice] = useState(initial?.price ?? 0);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const body = { el, en, descEl, descEn, price: Number(price) };
      if (initial?.id) {
        await api.put(
          `/admin/categories/${categoryId}/items/${initial.id}`,
          body,
        );
      } else {
        await api.post(`/admin/categories/${categoryId}/items`, body);
      }
      await onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={initial?.id ? "Επεξεργασία προϊόντος" : "Νέο προϊόν"}>
      <form onSubmit={save} className="space-y-3">
        <Field
          label="Όνομα (Ελληνικά)"
          required
          value={el}
          onChange={setEl}
          testid="item-el"
          placeholder="π.χ. Burrata"
        />
        <Field
          label="Όνομα (Αγγλικά)"
          required
          value={en}
          onChange={setEn}
          testid="item-en"
          placeholder="e.g. Burrata"
        />
        <Field
          label="Τιμή (€)"
          type="number"
          step="0.5"
          required
          value={price}
          onChange={(v) => setPrice(v)}
          testid="item-price"
          placeholder="π.χ. 17"
        />
        <Field
          label="Περιγραφή (Ελληνικά)"
          textarea
          value={descEl}
          onChange={setDescEl}
          testid="item-descEl"
          placeholder="π.χ. Ποικιλία από τομάτες & τοματίνια / μάνγκο / βασιλικός / τσίλι / σάλτσα nuoc cham"
        />
        <Field
          label="Περιγραφή (Αγγλικά)"
          textarea
          value={descEn}
          onChange={setDescEn}
          testid="item-descEn"
          placeholder="e.g. Mixed tomatoes & cherry tomatoes / mango / basil / chilli / nuoc cham sauce"
        />
        {err && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border/80 bg-background/40 px-4 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Άκυρο
          </button>
          <button
            data-testid="item-save"
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-gold-muted px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-widest text-background shadow-[0_4px_14px_-4px_rgba(212,170,90,0.5)] hover:brightness-110 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Αποθήκευση…" : "Αποθήκευση"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------ Category Form Modal ------------------------ */
function CategoryFormModal({ groups, categories = [], initial, onClose, onSaved }) {
  const initialGroup = initial?.group_id ?? groups[0]?.id ?? "food";
  // For new categories, append to end of the group
  const computeNextOrder = (gid) => {
    const orders = categories
      .filter((c) => c.group_id === gid)
      .map((c) => c.sort_order ?? 0);
    return orders.length ? Math.max(...orders) + 1 : 0;
  };

  const [id, setId] = useState(initial?.id ?? "");
  const [groupId, setGroupId] = useState(initialGroup);
  const [el, setEl] = useState(initial?.el ?? "");
  const [en, setEn] = useState(initial?.en ?? "");
  const [icon, setIcon] = useState(initial?.icon ?? "UtensilsCrossed");
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const body = {
        id: id.trim(),
        group_id: groupId,
        sort_order: initial?.sort_order ?? computeNextOrder(groupId),
        el,
        en,
        icon,
      };
      if (initial) {
        await api.put(`/admin/categories/${initial.id}`, body);
      } else {
        await api.post(`/admin/categories`, body);
      }
      await onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title={initial ? "Επεξεργασία κατηγορίας" : "Νέα κατηγορία"}
    >
      <form onSubmit={save} className="space-y-3">
        <Field
          label="ID (slug, μόνο λατινικά, π.χ. mezedakia)"
          required
          value={id}
          onChange={setId}
          testid="cat-id"
          placeholder="π.χ. mezedakia"
        />
        <label className="block">
          <span className="text-[0.6rem] uppercase tracking-[0.25em] text-gold-muted">
            Ομάδα
          </span>
          <select
            data-testid="cat-group"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-border/80 bg-background/40 px-3 py-3 text-sm text-foreground outline-none focus:border-gold/60"
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.el}
              </option>
            ))}
          </select>
        </label>
        <Field
          label="Όνομα (Ελληνικά)"
          required
          value={el}
          onChange={setEl}
          testid="cat-el"
          placeholder="π.χ. Μεζεδάκια"
        />
        <Field
          label="Όνομα (Αγγλικά)"
          required
          value={en}
          onChange={setEn}
          testid="cat-en"
          placeholder="e.g. Small plates"
        />
        <label className="block">
          <span className="text-[0.6rem] uppercase tracking-[0.25em] text-gold-muted">
            Εικονίδιο
          </span>
          <div className="mt-1.5">
            <IconPicker value={icon} onChange={setIcon} testid="cat-icon" />
          </div>
        </label>
        <p className="rounded-xl border border-gold/15 bg-gold/5 px-3 py-2 text-[0.65rem] leading-relaxed text-muted-foreground">
          💡 Η σειρά εμφάνισης ρυθμίζεται σύροντας τις κατηγορίες στο πλάι.
        </p>
        {err && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border/80 bg-background/40 px-4 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Άκυρο
          </button>
          <button
            data-testid="cat-save"
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-gold-muted px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-widest text-background shadow-[0_4px_14px_-4px_rgba(212,170,90,0.5)] hover:brightness-110 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Αποθήκευση…" : "Αποθήκευση"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------ Info Manager ------------------------ */
function InfoManager({ info, reload }) {
  const [form, setForm] = useState(info);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setOk(false);
    setSaving(true);
    try {
      await api.put("/admin/info", form);
      await reload();
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-8">
      <Section title="Hero — Πάνω μέρος">
        <Field label="Eyebrow (Ελληνικά)" value={form.eyebrow_el} onChange={set("eyebrow_el")} testid="info-eyebrow-el" />
        <Field label="Eyebrow (Αγγλικά)" value={form.eyebrow_en} onChange={set("eyebrow_en")} testid="info-eyebrow-en" />
        <Field label="Τίτλος 1 (Ελληνικά)" value={form.title1_el} onChange={set("title1_el")} testid="info-title1-el" />
        <Field label="Τίτλος 1 (Αγγλικά)" value={form.title1_en} onChange={set("title1_en")} testid="info-title1-en" />
        <Field label="Τίτλος 2 — italic gold (Ελληνικά)" value={form.title2_el} onChange={set("title2_el")} testid="info-title2-el" />
        <Field label="Τίτλος 2 — italic gold (Αγγλικά)" value={form.title2_en} onChange={set("title2_en")} testid="info-title2-en" />
        <Field label="Περιγραφή (Ελληνικά)" textarea value={form.desc_el} onChange={set("desc_el")} testid="info-desc-el" />
        <Field label="Περιγραφή (Αγγλικά)" textarea value={form.desc_en} onChange={set("desc_en")} testid="info-desc-en" />
      </Section>

      <Section title="Επικοινωνία">
        <Field label="Διεύθυνση γραμμή 1 (Ελληνικά)" value={form.address_el} onChange={set("address_el")} testid="info-addr1-el" />
        <Field label="Διεύθυνση γραμμή 1 (Αγγλικά)" value={form.address_en} onChange={set("address_en")} testid="info-addr1-en" />
        <Field label="Διεύθυνση γραμμή 2 (Ελληνικά)" value={form.address_line2_el} onChange={set("address_line2_el")} testid="info-addr2-el" />
        <Field label="Διεύθυνση γραμμή 2 (Αγγλικά)" value={form.address_line2_en} onChange={set("address_line2_en")} testid="info-addr2-en" />
        <Field label="Τηλέφωνο" value={form.phone} onChange={set("phone")} testid="info-phone" />
        <Field label="Ώρες λειτουργίας (Ελληνικά)" value={form.hours_el} onChange={set("hours_el")} testid="info-hours-el" />
        <Field label="Ώρες λειτουργίας (Αγγλικά)" value={form.hours_en} onChange={set("hours_en")} testid="info-hours-en" />
      </Section>

      <Section title="Social links">
        <Field label="Facebook URL" value={form.facebook_url} onChange={set("facebook_url")} testid="info-facebook" />
        <Field label="Instagram URL" value={form.instagram_url} onChange={set("instagram_url")} testid="info-instagram" />
        <Field label="Website URL" value={form.website_url} onChange={set("website_url")} testid="info-website" />
      </Section>

      <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card/80 px-4 py-3 backdrop-blur-xl">
        <div className="text-xs">
          {err && <span className="text-destructive">{err}</span>}
          {ok && <span className="text-gold">Αποθηκεύτηκε ✓</span>}
        </div>
        <button
          data-testid="info-save"
          type="submit"
          disabled={saving}
          className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-gold-muted px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-background shadow-[0_4px_14px_-4px_rgba(212,170,90,0.5)] hover:brightness-110 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "Αποθήκευση…" : "Αποθήκευση"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------ Sortable Category Sidebar ------------------------ */
function SortableCategoryGroup({ group, categories, activeCatId, onSelect, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [order, setOrder] = useState(() => categories.map((c) => c.id));
  // Resync when categories change (add/delete/server reload)
  if (
    order.length !== categories.length ||
    order.some((id) => !categories.find((c) => c.id === id))
  ) {
    setOrder(categories.map((c) => c.id));
  }

  const ordered = order.map((id) => categories.find((c) => c.id === id)).filter(Boolean);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(newOrder);
    onReorder(newOrder);
  };

  return (
    <div>
      <p className="px-2 py-1 text-[0.55rem] uppercase tracking-[0.25em] text-muted-foreground">
        {group.el}
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="mt-1 space-y-0.5">
            {ordered.map((c) => (
              <SortableCategoryRow
                key={c.id}
                category={c}
                isActive={activeCatId === c.id}
                onSelect={() => onSelect(c.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableCategoryRow({ category, isActive, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`cat-row-${category.id}`}
      className={
        "flex items-center gap-1 rounded-lg pl-1 pr-2 transition-colors " +
        (isActive ? "bg-gold/15 ring-1 ring-gold/40" : "hover:bg-card/80") +
        (isDragging ? " bg-card/95 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.6)]" : "")
      }
    >
      <button
        {...attributes}
        {...listeners}
        data-testid={`cat-drag-${category.id}`}
        aria-label="Σύρε"
        className="flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground/50 hover:text-gold active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button
        data-testid={`cat-${category.id}`}
        onClick={onSelect}
        className={
          "flex flex-1 items-center justify-between gap-2 py-2 pr-1 text-left text-sm " +
          (isActive ? "text-gold" : "text-foreground")
        }
      >
        <span className="truncate font-display italic">{category.el}</span>
        <span className="shrink-0 text-[0.6rem] uppercase tracking-widest text-muted-foreground">
          {category.items.length}
        </span>
      </button>
    </div>
  );
}

/* ------------------------ Sortable Item List ------------------------ */
function SortableItemList({ category, onEdit, onDelete, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Local order, synced with category prop changes
  const [order, setOrder] = useState(() => category.items.map((i) => i.id));
  // Keep order synced if items change (add/delete/edit)
  if (
    order.length !== category.items.length ||
    order.some((id) => !category.items.find((i) => i.id === id))
  ) {
    setOrder(category.items.map((i) => i.id));
  }

  if (category.items.length === 0) {
    return (
      <p className="py-12 text-center text-sm italic text-muted-foreground">
        Δεν υπάρχουν προϊόντα ακόμα. Πάτησε «Νέο προϊόν» για να ξεκινήσεις.
      </p>
    );
  }

  const items = order
    .map((id) => category.items.find((i) => i.id === id))
    .filter(Boolean);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(newOrder);
    onReorder(newOrder);
  };

  return (
    <>
      <p className="mb-2 flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.25em] text-gold-muted">
        <GripVertical className="h-3 w-3" />
        Σύρε για να αλλάξεις σειρά
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div data-testid="sortable-list" className="space-y-0">
            {items.map((item) => (
              <SortableRow
                key={item.id}
                item={item}
                onEdit={() => onEdit(item)}
                onDelete={() => onDelete(item)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
}

function SortableRow({ item, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-item-${item.id}`}
      className={
        "group flex items-start gap-3 border-b border-border/40 py-4 last:border-b-0 " +
        (isDragging
          ? "rounded-xl border border-gold/40 bg-card/80 shadow-[0_12px_40px_-10px_rgba(0,0,0,0.6)]"
          : "")
      }
    >
      <button
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${item.id}`}
        aria-label="Σύρε για αναδιάταξη"
        className="mt-1 flex h-7 w-7 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-card/80 hover:text-gold active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="truncate font-display text-lg text-foreground sm:text-xl">
            {item.el}
          </p>
          <span className="shrink-0 font-display text-lg text-gold sm:text-xl">
            {item.price}€
          </span>
        </div>
        {item.en !== item.el && (
          <p className="mt-0.5 text-[0.65rem] uppercase tracking-[0.18em] text-gold-muted/80">
            {item.en}
          </p>
        )}
        {item.descEl && (
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground sm:text-sm">
            {item.descEl}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          data-testid={`edit-item-${item.id}`}
          onClick={onEdit}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:border-gold/40 hover:text-gold"
          aria-label="Επεξεργασία"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          data-testid={`delete-item-${item.id}`}
          onClick={onDelete}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
          aria-label="Διαγραφή"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------ Reusable bits ------------------------ */
function Field({ label, value, onChange, type = "text", required, textarea, step, testid, placeholder, hint }) {
  return (
    <label className="block">
      <span className="text-[0.6rem] uppercase tracking-[0.25em] text-gold-muted">
        {label}
      </span>
      {textarea ? (
        <textarea
          data-testid={testid}
          required={required}
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1.5 w-full rounded-xl border border-border/80 bg-background/40 px-3.5 py-3 text-sm text-foreground outline-none focus:border-gold/60 placeholder:text-muted-foreground/50"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          step={step}
          required={required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1.5 w-full rounded-xl border border-border/80 bg-background/40 px-3.5 py-3 text-sm text-foreground outline-none focus:border-gold/60 placeholder:text-muted-foreground/50"
        />
      )}
      {hint && (
        <span className="mt-1 block text-[0.65rem] leading-relaxed text-muted-foreground/70">
          {hint}
        </span>
      )}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card/40 p-5 sm:p-6">
      <p className="mb-4 text-[0.65rem] uppercase tracking-[0.3em] text-gold">
        {title}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center px-4 py-6 sm:items-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.97 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gold/20 bg-card/95 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border/40 px-5 py-4 sm:px-6">
          <h3 className="font-display text-2xl italic text-foreground">{title}</h3>
          <button
            data-testid="modal-close"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground transition-colors hover:text-gold"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
      </motion.div>
    </div>
  );
}

function ConfirmModal({ title, message, onCancel, onConfirm }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <p className="text-sm text-muted-foreground">{message}</p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-border/80 bg-background/40 px-4 py-2 text-[0.65rem] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          Άκυρο
        </button>
        <button
          type="button"
          data-testid="confirm-delete"
          onClick={onConfirm}
          className="flex items-center gap-1.5 rounded-full bg-destructive/90 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-widest text-destructive-foreground hover:bg-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Διαγραφή
        </button>
      </div>
    </Modal>
  );
}
